/**
 * New Joinee Form — locked decisions (Phase 1+)
 *
 * Mapping: Candidate Info=A, Employment=B, Education=C, Bank=D, Insurance=E
 * Q1: Rebuild UI shell + Formik/Yup; reuse APIs; modify API only when needed
 * Q2: Experience From/To dates on Candidate Info; derive years/months for DB/display
 * Q3: Prior experience checked = has experience; unchecked = fresher (skip Employment)
 * Q4: Signature legal Figma text + store boolean in DB
 * Q5: Acknowledge — best: legal signature consent on Candidate Info;
 *     keep “details are true” on later sections until those are redesigned
 * Q6: Relation = fixed dropdown
 * Q7: Religion = required text input
 * Q8: Defaults — Gender Male, Marital Married, Passport No, Prior Yes,
 *     Same as Current unchecked, ICICI “I don’t have” checked
 * Q9: Uncheck “I don’t have ICICI” → fields required
 * Q10: New UI for joinee + admin
 * Q11: Phase 1 = shell + Candidate Info; then Employment… (best) — LOCKED
 * Skills: keep existing chips/tags SelectSkills — LOCKED
 * PF/UAN/ESIC: standard format hover hints — LOCKED
 * Education: default 10th + 12th/Diploma rows — LOCKED
 * Insurance members: keep existing system behavior — LOCKED
 */

export var JOINEE_FORM_COLORS = {
  label: "#171A1C",
  heading: "#171A1C",
  placeholder: "#848F9A",
  primary: "#0011D3",
  navy: "#050A44",
  legal: "#2E3338",
  border: "#C7CCD1",
  borderSecondary: "#E6E8EA",
  surface: "#FDFDFD",
  error: "#D32F2F",
  inverse: "#F9FAFA",
};

export var JOINEE_FORM_FONT =
  '"Poppins", "TTHoves", "Segoe UI", sans-serif';

export var JOINEE_FORM_STEPS = [
  {
    id: "candidate",
    label: "Candidate Info",
    title: "New Joinee Form",
    subtitle: "Enter your details in each section",
  },
  {
    id: "employment",
    label: "Employment",
    title: "Employment History",
    subtitle:
      "Enter details of your previous employment to help us maintain an accurate professional record.",
  },
  {
    id: "education",
    label: "Education",
    title: "Education",
    subtitle:
      "Provide details of your educational background, including schools, colleges, and qualifications.",
  },
  {
    id: "bank",
    label: "Bank",
    title: "Bank Details",
    subtitle:
      "Provide your bank account details for salary processing and other official transactions.",
  },
  {
    id: "insurance",
    label: "Insurance",
    title: "Insurance",
    subtitle:
      "Complete your insurance information by providing coverage details and adding eligible dependents.",
  },
];

export var BLOOD_GROUPS = [
  "A+",
  "A-",
  "B+",
  "B-",
  "O+",
  "O-",
  "AB+",
  "AB-",
];

export var GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Prefer Not to Answer", label: "Prefer Not to Answer" },
];

export var MARITAL_OPTIONS = [
  { value: "Married", label: "Married" },
  { value: "Unmarried", label: "Unmarried" },
  { value: "Widow", label: "Widow" },
  { value: "Widower", label: "Widower" },
  { value: "Divorcee", label: "Divorcee" },
];

export var RELATION_OPTIONS = [
  { value: "mother", label: "Mother" },
  { value: "father", label: "Father" },
  { value: "spouse", label: "Spouse" },
  { value: "sister", label: "Sister" },
  { value: "brother", label: "Brother" },
  { value: "son", label: "Son" },
  { value: "daughter", label: "Daughter" },
  { value: "guardian", label: "Guardian" },
  { value: "friend", label: "Friend" },
  { value: "other", label: "Other" },
];

export var INSURANCE_RELATIONSHIP_OPTIONS = [
  { value: "", label: "Select Relationship" },
  { value: "Self", label: "Self" },
  { value: "Spouse", label: "Spouse" },
  { value: "Daughter", label: "Daughter" },
  { value: "Son", label: "Son" },
  { value: "Mother", label: "Mother" },
  { value: "Father", label: "Father" },
  { value: "Mother-in-law", label: "Mother-in-law" },
  { value: "Father-in-law", label: "Father-in-law" },
];

export function emptyInsuranceMember(userId, relationship) {
  return {
    userId: userId || "",
    name: "",
    relationship: relationship || "",
    dob: null,
  };
}

export function getInsurancePolicyYearLabel() {
  var now = new Date();
  var year = now.getFullYear();
  var month = now.getMonth();
  if (month < 6) {
    return (
      "Jun'" +
      String(year - 1).slice(-2) +
      "-Jun'" +
      String(year).slice(-2)
    );
  }
  return (
    "Jun'" +
    String(year).slice(-2) +
    "-Jun'" +
    String(year + 1).slice(-2)
  );
}

export var NATURE_OF_RESIDENCY = [
  { value: "Own-house", label: "Own House" },
  { value: "Rented", label: "Rented" },
  { value: "Parental", label: "Parental" },
];

export var FOOD_COUPON_OPTIONS = [
  { value: "", label: "Select Food Coupon Value" },
  { value: "1100", label: "1100rs" },
  { value: "2200", label: "2200rs" },
];

export var NATURE_OF_BUSINESS_OPTIONS = [
  { value: "", label: "Select Nature of Business" },
  { value: "Service Industry", label: "Service Industry" },
  { value: "It", label: "IT" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Cooperative", label: "Cooperative" },
  { value: "Marketing", label: "Marketing" },
  { value: "Public-Sector", label: "Public sector" },
  { value: "Banking", label: "Banking" },
];

export var TYPE_OF_EMPLOYMENT_OPTIONS = [
  { value: "", label: "Select Type of Employment" },
  { value: "Regular", label: "Regular" },
  { value: "Contract", label: "Contract" },
  { value: "Adhoc", label: "Adhoc" },
  { value: "Trainee ", label: "Trainee" },
];

export function emptyEmployerRow(userId) {
  return {
    userId: userId || "",
    employerName: "",
    companyContact: "",
    department: "",
    employeeCode: "",
    designation: "",
    remuneration: "",
    reportedTo: "",
    reportedPersonDesig: "",
    natureOfBusiness: "",
    typeOfEmployment: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    doj: null,
    dol: null,
    reasonForLeaving: "",
    referenceContactPerson1Name: "",
    referenceContactPerson1Contact: "",
    referenceContactPerson2Name: "",
    referenceContactPerson2Contact: "",
  };
}

export var LOCKED_QUALIFICATIONS = ["10th", "12th/diploma", "Graduation"];

export var QUALIFICATION_OPTIONS = [
  { value: "", label: "Select the Qualification" },
  { value: "10th", label: "10th" },
  { value: "12th/diploma", label: "12th/Diploma" },
  { value: "Graduation", label: "Graduation" },
  { value: "Masters", label: "Masters" },
  { value: "PhD", label: "PhD" },
  { value: "Other", label: "Other" },
];

export var ADDABLE_QUALIFICATION_OPTIONS = [
  { value: "", label: "Select the Qualification" },
  { value: "Masters", label: "Masters" },
  { value: "PhD", label: "PhD" },
  { value: "Other", label: "Other" },
];

export function emptyQualificationRow(userId, qualification) {
  return {
    userId: userId || "",
    qualification: qualification || "",
    fromDate: null,
    toDate: null,
    specialisedIn: "",
    university: "",
    registrationNo: "",
    result: "",
    collegeName: "",
  };
}

export function defaultQualificationRows(userId) {
  return [
    emptyQualificationRow(userId, "10th"),
    emptyQualificationRow(userId, "12th/diploma"),
    emptyQualificationRow(userId, "Graduation"),
  ];
}

export var EXPERIENCE_YEAR_OPTIONS = (function () {
  var list = [{ value: "", label: "Select Year" }];
  for (var y = 0; y <= 24; y++) {
    list.push({ value: String(y), label: String(y) });
  }
  list.push({ value: "25+", label: "25+" });
  return list;
})();

export var EXPERIENCE_MONTH_OPTIONS = (function () {
  var list = [{ value: "", label: "Select Month" }];
  for (var m = 1; m <= 12; m++) {
    list.push({ value: String(m), label: String(m) });
  }
  return list;
})();

export var FIELD_HINTS = {
  pf: "22 characters: state + office + establishment + extension + member, e.g. MHBAN00000640000000125 or MH/BAN/0000064/000/0000125. Leave blank if not allotted.",
  uan: "12-digit Universal Account Number issued by EPFO, e.g. 100123456789. Leave blank if not allotted.",
  esic: "10-digit ESIC insurance number, e.g. 3100123456. Leave blank if not applicable.",
};

export var SIGNATURE_LEGAL_TEXT =
  "I understand that this document constitutes the legal representation of my electronic signature, and I agree to the terms and conditions stated above.";

/** Diff months between two dates; returns { years, months } */
export function diffYearsMonths(fromDate, toDate) {
  if (!fromDate || !toDate) {
    return { years: "", months: "" };
  }
  var from = new Date(fromDate);
  var to = new Date(toDate);
  if (isNaN(from.getTime()) || isNaN(to.getTime()) || to < from) {
    return { years: "", months: "" };
  }
  var months =
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth());
  if (to.getDate() < from.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    months = 0;
  }
  return {
    years: String(Math.floor(months / 12)),
    months: String(months % 12),
  };
}

export function emptyContactRow() {
  return { name: "", contact: "", relationship: "" };
}
